import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { ArrowLeft, X } from 'lucide-react';

import type { ChannelKind, GroupDetail } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { EntryIcon } from './EntryIcon';
import { GroupKindField } from './GroupKindField';
import { MemberPicker } from './MemberPicker';
import { addEntry, removeEntry, type EntryRef } from './memberEntries';
import { useCreateGroup } from './useGroupAdminQueries';

const NAME_TAKEN = 'A group with this name already exists';
/** The 404s a person can act on by removing the entry; shown under the chips. */
const MEMBER_MESSAGES = new Set(['User not found', 'Role not found']);

type Chip = EntryRef & { label: string };

type NewGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (detail: GroupDetail) => void;
};

export function NewGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: NewGroupDialogProps) {
  const createGroup = useCreateGroup();
  const showError = useStore(state => state.showError);
  const user = useStore(state => state.user);

  const creatorChips = (): Chip[] =>
    user ? [{ kind: 'user', id: user.id, label: user.name }] : [];

  const [name, setName] = useState('');
  const [chips, setChips] = useState<Chip[]>(creatorChips);
  const [kind, setKind] = useState<ChannelKind>('conversation');
  const [nameError, setNameError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const close = () => {
    setName('');
    setChips(creatorChips());
    setKind('conversation');
    setNameError(null);
    setMembersError(null);
    onOpenChange(false);
  };

  const create = async () => {
    setNameError(null);
    setMembersError(null);
    setPending(true);
    try {
      const detail = await createGroup.mutateAsync({
        name: name.trim(),
        members: chips.map(({ label: _label, ...entry }) => entry),
        kind,
      });
      close();
      onCreated(detail);
    } catch (error) {
      const message = getBusinessErrorMessage(error);
      if (getHttpStatus(error) === 409 && message === NAME_TAKEN) {
        setNameError(message);
      } else if (message && MEMBER_MESSAGES.has(message)) {
        setMembersError(message);
      } else {
        console.error('Failed to create group', error);
        showError('Could not save the group');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && !pending && close()}>
      <DialogContent
        aria-describedby={undefined}
        className='inset-0 h-full max-h-none max-w-none translate-x-0 translate-y-0 content-start overflow-y-auto rounded-none md:inset-auto md:left-[50%] md:top-[50%] md:h-auto md:max-h-[calc(100dvh-2rem)] md:max-w-lg md:translate-x-[-50%] md:translate-y-[-50%] md:content-normal md:rounded-lg'
      >
        <Button
          variant='ghost'
          size='icon'
          aria-label='Back'
          className='md:hidden'
          onClick={close}
          disabled={pending}
        >
          <ArrowLeft />
        </Button>
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
        </DialogHeader>

        <div className='flex min-w-0 flex-col gap-2'>
          <label htmlFor='new-group-name' className='text-sm font-medium'>
            Name
          </label>
          <Input
            id='new-group-name'
            value={name}
            maxLength={80}
            onChange={event => setName(event.target.value)}
          />
          {nameError && <p className='text-xs text-destructive'>{nameError}</p>}
        </div>
        <GroupKindField value={kind} onChange={setKind} disabled={pending} />

        <div className='flex min-w-0 flex-col gap-2'>
          <h3 className='text-sm font-medium'>Members</h3>
          <MemberPicker
            selected={chips}
            onPick={(entry, label) =>
              setChips(current =>
                addEntry(current, entry) === current
                  ? current
                  : [...current, { ...entry, label }]
              )
            }
          />
          {chips.length > 0 && (
            <ul className='flex flex-col gap-1'>
              {chips.map(chip => (
                <li
                  key={
                    chip.kind === 'source'
                      ? `source:${chip.clinic_id ?? ''}:${chip.discipline ?? ''}`
                      : `${chip.kind}:${chip.id}`
                  }
                  className='flex min-w-0 items-center gap-2 rounded-md border px-2'
                >
                  <EntryIcon entry={chip} />
                  <span className='truncate text-sm'>{chip.label}</span>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='ml-auto'
                    aria-label={`Remove ${chip.label}`}
                    onClick={() =>
                      setChips(current => {
                        const kept = removeEntry(current, chip);
                        return current.filter(c => kept.includes(c));
                      })
                    }
                  >
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {membersError && (
            <p className='text-xs text-destructive'>{membersError}</p>
          )}
        </div>

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={create} disabled={name.trim() === '' || pending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
